/**
 * DecisionEase — Push Notification Hook (Phase 2)
 * Manages Web Push subscription lifecycle:
 * - Fetches VAPID public key from backend
 * - Requests notification permission
 * - Subscribes/unsubscribes with the Push API
 * - Syncs subscription to backend
 */
import { useState, useCallback, useEffect } from "react";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";
const PUSH_SW_PATH = "/push-sw.js";

function shouldDisableServiceWorker() {
  if (typeof window === "undefined") return true;
  return (
    process.env.NODE_ENV !== "production" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

export type PushReminderKind = "check_in" | "recovery";

export interface PushReminderPreview {
  kind: PushReminderKind;
  allowed: boolean;
  reason?: string | null;
  title: string;
  body: string;
  url: string;
  tag: string;
  task_title?: string | null;
  sent?: number;
  failed?: number;
  error?: string | null;
}

async function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    shouldDisableServiceWorker()
  ) {
    return null;
  }

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) {
    return existing;
  }

  if (!existing) {
    await navigator.serviceWorker.register(PUSH_SW_PATH);
  }

  return navigator.serviceWorker.ready;
}

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer.slice(0);
}

export function usePush() {
  const [supported, setSupported] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reminderPreview, setReminderPreview] = useState<PushReminderPreview | null>(null);
  const [reminderLoadingKind, setReminderLoadingKind] = useState<PushReminderKind | null>(null);

  useEffect(() => {
    const isSupported =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window &&
      !shouldDisableServiceWorker();

    setSupported(isSupported);

    if (isSupported) {
      setPermission(Notification.permission);
      fetch(`${API_BASE}/api/push/vapid-key`, { cache: "no-store" })
        .then((response) => {
          setConfigured(response.ok);
        })
        .catch(() => {
          setConfigured(false);
        });

      // Check if already subscribed
      ensureServiceWorkerRegistration()
        .then((reg) => reg?.pushManager.getSubscription() ?? null)
        .then((sub) => {
          setSubscribed(sub !== null);
        })
        .catch((err) => {
          console.error("Service worker registration failed:", err);
        });
    }
    if (!isSupported) {
      setConfigured(false);
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || configured !== true) return false;
    setLoading(true);

    try {
      // 1. Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return false;
      }

      // 2. Get VAPID public key from backend
      const vapidRes = await fetch(`${API_BASE}/api/push/vapid-key`);
      if (!vapidRes.ok) {
        setLoading(false);
        return false;
      }
      const { public_key } = await vapidRes.json();

      // 3. Subscribe via Push API
      const reg = await ensureServiceWorkerRegistration();
      if (!reg) {
        setLoading(false);
        return false;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(public_key),
      });

      // 4. Send subscription to backend
      const token = localStorage.getItem("de_access_token");
      const subJson = sub.toJSON();
      const res = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          user_agent: navigator.userAgent,
        }),
      });

      if (res.ok) {
        setSubscribed(true);
        setLoading(false);
        return true;
      }

      setLoading(false);
      return false;
    } catch (err) {
      console.error("Push subscription failed:", err);
      setLoading(false);
      return false;
    }
  }, [configured, supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setLoading(true);

    try {
      const reg = await ensureServiceWorkerRegistration();
      if (!reg) {
        setSubscribed(false);
        setLoading(false);
        return true;
      }
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setSubscribed(false);
        setLoading(false);
        return true;
      }

      // Unsubscribe from Push API
      await sub.unsubscribe();

      // Remove from backend
      const token = localStorage.getItem("de_access_token");
      await fetch(`${API_BASE}/api/push/unsubscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });

      setSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
      setLoading(false);
      return false;
    }
  }, [supported]);

  const requestReminder = useCallback(async (
    kind: PushReminderKind,
    mode: "preview" | "send",
  ): Promise<PushReminderPreview | null> => {
    setReminderLoadingKind(kind);
    try {
      const token = localStorage.getItem("de_access_token");
      const response = await fetch(
        `${API_BASE}/api/push/${mode === "preview" ? "reminder-preview" : "reminder-send"}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ kind }),
        },
      );

      if (!response.ok) {
        setReminderLoadingKind(null);
        return null;
      }

      const data = (await response.json()) as PushReminderPreview;
      setReminderPreview(data);
      setReminderLoadingKind(null);
      return data;
    } catch (err) {
      console.error("Reminder request failed:", err);
      setReminderLoadingKind(null);
      return null;
    }
  }, []);

  const previewReminder = useCallback(
    async (kind: PushReminderKind) => requestReminder(kind, "preview"),
    [requestReminder],
  );

  const sendReminder = useCallback(
    async (kind: PushReminderKind) => requestReminder(kind, "send"),
    [requestReminder],
  );

  return {
    supported,
    configured,
    permission,
    subscribed,
    loading,
    subscribe,
    unsubscribe,
    reminderPreview,
    reminderLoadingKind,
    previewReminder,
    sendReminder,
  };
}
